import type { SupabaseClient } from "@supabase/supabase-js";

export interface ManagerMember {
  userId: string;
  fullName: string;
  profileActive: boolean;
  role: "owner" | "manager" | "operator";
  membershipActive: boolean;
  isDutyWorker: boolean;
}

export interface ManagerAdministrationCapabilities {
  canManageUsers: boolean;
  canManagePermissions: boolean;
  canManageDuty: boolean;
}

export interface ManagerAdministrationSnapshot {
  members: ManagerMember[];
  capabilities: ManagerAdministrationCapabilities;
}

export interface MemberPermissionRow {
  permissionKey: string;
  module: string;
  label: string;
  managerConfigurable: boolean;
  roleAllowed: boolean;
  overrideDecision: boolean | null;
  effectiveAllowed: boolean;
  effectiveSource: "role_default" | "user_override";
}

function rowRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(rowRecord) : [];
}

function firstRow(value: unknown): Record<string, unknown> {
  return rows(value)[0] ?? rowRecord(value);
}

function role(value: unknown): ManagerMember["role"] {
  if (value === "owner" || value === "manager" || value === "operator") return value;
  throw new Error("M4A_MEMBER_ROLE_INVALID");
}

async function permissionAllowed(
  client: SupabaseClient,
  farmId: string,
  permission: string,
): Promise<boolean> {
  const { data, error } = await client.rpc("permission_decide", {
    target_farm_id: farmId,
    requested_permission: permission,
  });
  if (error) throw error;
  return firstRow(data).allowed === true;
}

export async function loadManagerAdministrationSnapshot(
  client: SupabaseClient,
  farmId: string,
): Promise<ManagerAdministrationSnapshot> {
  const [membersResult, canManageUsers, canManagePermissions, canManageDuty] = await Promise.all([
    client.rpc("farm_member_admin_list_v1", { target_farm_id: farmId }),
    permissionAllowed(client, farmId, "users.manage"),
    permissionAllowed(client, farmId, "permissions.manage"),
    permissionAllowed(client, farmId, "duty.manage"),
  ]);

  if (membersResult.error) throw membersResult.error;

  return {
    members: rows(membersResult.data).map((item) => ({
      userId: String(item.user_id),
      fullName: String(item.full_name ?? "کاربر"),
      profileActive: item.profile_active === true,
      role: role(item.role),
      membershipActive: item.membership_active === true,
      isDutyWorker: item.is_duty_worker === true,
    })),
    capabilities: {
      canManageUsers,
      canManagePermissions,
      canManageDuty,
    },
  };
}

export async function loadMemberPermissionMatrix(
  client: SupabaseClient,
  farmId: string,
  userId: string,
): Promise<MemberPermissionRow[]> {
  const { data, error } = await client.rpc("farm_member_permission_matrix_v1", {
    target_farm_id: farmId,
    target_user_id: userId,
  });
  if (error) throw error;

  return rows(data).map((item) => ({
    permissionKey: String(item.permission_key),
    module: String(item.module),
    label: String(item.label),
    managerConfigurable: item.manager_configurable === true,
    roleAllowed: item.role_allowed === true,
    overrideDecision:
      item.override_decision === true ? true : item.override_decision === false ? false : null,
    effectiveAllowed: item.effective_allowed === true,
    effectiveSource: item.effective_source === "user_override" ? "user_override" : "role_default",
  }));
}

export function memberRoleLabel(value: ManagerMember["role"]): string {
  if (value === "owner") return "مالک";
  if (value === "manager") return "مدیر";
  return "کارگر";
}
